import { BrowserRouter, Routes, Route, Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { AccountsProvider } from './context/AccountsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { HomeRedirect } from './components/HomeRedirect';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { SignupPage } from './pages/Signup';
import { DashboardPage } from './pages/Dashboard';
import { AccountViewPage } from './pages/AccountView';
import { AccountSettingsPage } from './pages/AccountSettings';
import { SettingsPage } from './pages/Settings';
import { PaymentsPage } from './pages/Payments';
import { AgencyPage } from './pages/Agency';
import { TeamPage } from './pages/Team';
import { CopyPage } from './pages/Copy';
import { StatisticsPage } from './pages/Statistics';
import { AccountCrmPage } from './pages/AccountCrm';
import { WorkspacePage } from './pages/Workspace';
import { BulkMediaPage } from './bulkMedia/BulkMediaPage';
import { VoicesPage } from './voices/VoicesPage';
import { DynamicListsPage } from './dynamicLists/DynamicListsPage';
import { TrackingLinksPage } from './trackingLinks/TrackingLinksPage';
import { PayoutsPage } from './payouts/PayoutsPage';
import { MassMessagesToolPage } from './massPages/MassMessagesToolPage';
import { ChatAssistantToolPage } from './massPages/ChatAssistantToolPage';
import { AutomatedMessagesRedirect, AutomatedMessagesToolPage } from './massPages/AutomatedMessagesToolPage';
import { WorkspaceProvider } from './workspace/WorkspaceProvider';
import { NyxHost } from './pet/Nyx';
import { CrmHostProvider } from './crm/CrmHostContext';
import { CrmFrameHost } from './crm/CrmFrameHost';
import { WelcomePage } from './pages/Welcome';
import { PartnersPage } from './pages/Partners';
import { NewUserOnboarding } from './components/Onboarding/NewUserOnboarding';
import { ConnectTelegram } from './components/Telegram/ConnectTelegram';
import { AddAccountFlow } from './components/Onboarding/AddAccountFlow';
import { AiSetupFlow } from './components/Onboarding/AiSetupFlow';
import { StartupScreen } from './brand/StartupScreen';
import { AppToastProvider } from './components/ToastProvider';
import { DesktopIntegration } from './desktop/DesktopIntegration';

/** /payments/usage[?tab=on-demand] → the AI Usage tab, keeping the On demand view. */
function UsageRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={params.get('tab') === 'on-demand' ? '/payments?tab=usage&mode=od' : '/payments?tab=usage'} replace />;
}

function AppRoutes() {
  const { t } = useTranslation('common');
  const { isAuthenticated, loading, needsTelegram } = useAuth();

  if (loading) {
    return (
      <StartupScreen label={t('loading')} />
    );
  }

  return (
    <Routes>
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/" replace /> : <SignupPage />}
      />
      <Route
        path="/connect-telegram"
        element={!isAuthenticated ? <Navigate to="/login" replace /> : needsTelegram ? <ConnectTelegram /> : <Navigate to="/" replace />}
      />
      <Route
        path="/onboarding"
        element={!isAuthenticated ? <Navigate to="/login" replace /> : needsTelegram ? <Navigate to="/connect-telegram" replace /> : <NewUserOnboarding />}
      />
      <Route
        path="/accounts/new"
        element={
          <ProtectedRoute>
            <AddAccountFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/:accountId/ai-setup"
        element={
          <ProtectedRoute>
            <AiSetupFlow />
          </ProtectedRoute>
        }
      />
      {/* The shell (sidebar + main) is mounted once for all of these, so moving
          between pages, accounts or views never reloads the Accounts list. */}
      <Route
        element={
          <ProtectedRoute>
            <Layout>
              <Outlet />
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/chat-assistant" element={<ChatAssistantToolPage />} />
        <Route path="/account/:accountId" element={<AccountViewPage />} />
        <Route path="/account/:accountId/crm/:page?" element={<AccountCrmPage />} />
        <Route path="/account/:accountId/settings" element={<AccountSettingsPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/payouts" element={<PayoutsPage />} />
        <Route path="/agency" element={<AgencyPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/copy" element={<CopyPage />} />
        <Route path="/bulk-media" element={<BulkMediaPage />} />
        <Route path="/audio" element={<VoicesPage />} />
        <Route path="/dynamic-lists" element={<DynamicListsPage />} />
        <Route path="/tracking-links" element={<TrackingLinksPage />} />
        <Route path="/mass-messages" element={<MassMessagesToolPage kind="mass" />} />
        <Route path="/priority-mass-messages" element={<MassMessagesToolPage kind="priority" />} />
        <Route path="/subscription-messages" element={<AutomatedMessagesToolPage kind="subscription" />} />
        <Route path="/online-messages" element={<AutomatedMessagesToolPage kind="online" />} />
        {/* The one Automated messages page became the two above. */}
        <Route path="/automated-messages" element={<AutomatedMessagesRedirect />} />
        <Route path="/workspace" element={<WorkspacePage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      {/* The old Payments sub-pages are tabs and dialogs on /payments now. */}
      <Route path="/payments/top-up" element={<Navigate to="/payments?topup=1" replace />} />
      <Route path="/payments/referrals" element={<Navigate to="/payments?tab=referral" replace />} />
      <Route path="/payments/usage" element={<UsageRedirect />} />
      <Route path="/payments/on-demand-usage" element={<Navigate to="/payments?tab=usage&mode=od" replace />} />
      <Route path="/payments/metered-usage" element={<Navigate to="/payments?tab=usage" replace />} />
      {/* CRM stats was replaced by Statistics. */}
      <Route path="/crm-stats" element={<Navigate to="/statistics" replace />} />
      {/* Dashboard, or Workspace for a CRM-only login (ADR 0031). */}
      <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AccountsProvider>
          <SidebarProvider>
            <CrmHostProvider>
              <WorkspaceProvider>
                <AppRoutes />
                <NyxHost />
                <DesktopIntegration />
              </WorkspaceProvider>
              <CrmFrameHost />
              <AppToastProvider />
            </CrmHostProvider>
          </SidebarProvider>
        </AccountsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
